import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { AuthService } from '../../../auth/services/auth.service';
import { FirmwareUpdateService } from '../../services/firmware-update.service';
import { ReceiversService } from '../../../shared/services/receivers.service';
import {
  FirmwareFile,
  PanelDevice,
  CompanyOption,
  QueueDevice,
} from '../../models/firmware-update.model';
import {
  FirmwareConfirmDialogComponent,
  ConfirmDialogData,
} from '../firmware-confirm-dialog/firmware-confirm-dialog.component';
import { FirmwareConfigDialogComponent } from '../firmware-config-dialog/firmware-config-dialog.component';

@Component({
  selector: 'app-firmware-update',
  templateUrl: './firmware-update.component.html',
  styleUrls: ['./firmware-update.component.scss'],
})
export class FirmwareUpdateComponent implements OnInit, OnDestroy {
  // Device list
  allDevices: PanelDevice[] = [];
  devices: PanelDevice[] = [];
  companies: CompanyOption[] = [];
  apiKeys: string[] = [];
  receiverOptions: { apiKey: string; name: string }[] = [];
  availableFirmware: FirmwareFile[] = [];

  // Filters
  selectedCompany = '';
  companySearchText = '';
  filteredCompanies: CompanyOption[] = [];
  selectedApiKey = '';
  selectedFirmwareType = '';
  macSearch = '';
  // Status visibility filters
  showUpToDate = true;
  showOutdated = true;
  showUnknown = true;

  // Selection
  selectAll = false;
  selectedMacs = new Set<string>();

  // State
  isLoading = false;
  isUpdating = false;
  currentView: 'devices' | 'progress' | 'history' = 'devices';

  // Global queue (replaces activeJob)
  queueDevices: QueueDevice[] = [];

  // History
  historyDevices: any[] = [];
  historyColumns: string[] = ['mac', 'status', 'firmwareVersion', 'error', 'updatedAt', 'actions'];
  isLoadingHistory = false;

  // Pagination
  totalDevices = 0;
  currentPage = 0;
  pageSize = 25;

  // Progress table columns
  displayedColumns: string[] = ['mac', 'receiver', 'status', 'progress', 'error', 'actions'];

  // MAC search debounce
  private macSearchSubject = new Subject<string>();
  private pollingInterval: any;

  constructor(
    public authService: AuthService,
    private firmwareService: FirmwareUpdateService,
    private receiversService: ReceiversService,
    private router: Router,
    private snackBar: MatSnackBar,
    private translate: TranslateService,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    if (!this.authService.hasRoleAdmin()) {
      this.router.navigate(['/maps']);
      return;
    }

    this.loadCompanies();
    this.loadAvailableFirmware();
    this.loadGlobalQueue();

    // Ensure receivers are loaded (they may not be if user navigates directly here)
    if (!this.receiversService.receivers || this.receiversService.receivers?.length === 0) {
      this.receiversService.getReceivers();
    }

    this.macSearchSubject
      .pipe(debounceTime(400), distinctUntilChanged())
      .subscribe(() => {
        this.currentPage = 0;
        this.loadDevices();
      });
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.macSearchSubject.complete();
  }

  // --- Data Loading ---

  loadCompanies(): void {
    // Use companies already available from the user session (loaded at login)
    const userCompanies = this.authService.user?.companies || [];
    this.companies = userCompanies.map((c: any) => ({ _id: c._id, name: c.name }));
    this.filteredCompanies = [...this.companies];
  }

  loadAvailableFirmware(): void {
    this.firmwareService.getAvailableFirmware().subscribe({
      next: (res) => {
        if (res.success) {
          this.availableFirmware = res.files;
        }
      },
      error: () => { },
    });
  }

  /**
   * Load the global queue — replaces the old loadActiveJobs().
   * If there are active/waiting devices, show the progress view and start polling.
   */
  loadGlobalQueue(): void {
    this.firmwareService.getGlobalQueue().subscribe({
      next: (res) => {
        if (res.success && res.data) {
          this.queueDevices = res.data;
          if (this.queueDevices.length > 0 && this.isQueueRunning) {
            this.isUpdating = true;
            this.currentView = 'progress';
            this.startPolling();
          }
        }
      },
      error: () => { },
    });
  }

  loadHistory(): void {
    this.isLoadingHistory = true;
    this.firmwareService.getRecentHistory(15).subscribe({
      next: (res) => {
        this.isLoadingHistory = false;
        if (res.success) {
          this.historyDevices = res.data;
        }
      },
      error: () => {
        this.isLoadingHistory = false;
      },
    });
  }

  switchView(view: 'devices' | 'progress' | 'history'): void {
    this.currentView = view;
    if (view === 'history' && this.historyDevices.length === 0) {
      this.loadHistory();
    }
  }

  openConfigDialog(): void {
    this.dialog.open(FirmwareConfigDialogComponent, {
      width: '680px',
      data: { companies: this.companies },
    });
  }

  loadDevices(): void {
    if (!this.selectedCompany) {
      this.allDevices = [];
      this.devices = [];
      this.totalDevices = 0;
      return;
    }

    this.isLoading = true;
    this.firmwareService
      .getDevices({
        company: this.selectedCompany,
        apiKey: this.selectedApiKey || undefined,
        mac: this.macSearch || undefined,
        page: 1,
        limit: 10000,
      })
      .subscribe({
        next: (res) => {
          this.isLoading = false;
          if (res.success) {
            this.allDevices = res.data.map((d) => ({
              ...d,
              selected: this.selectedMacs.has(d.mac),
              updateStatus: this.getUpdateStatus(d),
            }));
            this.applyClientFilters();
          }
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  applyClientFilters(): void {
    let filtered = this.allDevices;

    // Status filter
    if (!this.showUpToDate) filtered = filtered.filter((d) => d.updateStatus !== 'up_to_date');
    if (!this.showOutdated) filtered = filtered.filter((d) => d.updateStatus !== 'outdated');
    if (!this.showUnknown) filtered = filtered.filter((d) => d.updateStatus !== 'unknown');

    // Firmware type filter
    if (this.selectedFirmwareType) {
      filtered = filtered.filter((d) => d.firmwareType === this.selectedFirmwareType);
    }

    // Separate selected (from any source) and non-selected
    const selectedFromAll = this.allDevices.filter((d) => d.selected && !filtered.includes(d));
    const selectedInFiltered = filtered.filter((d) => d.selected);
    const unselectedInFiltered = filtered.filter((d) => !d.selected);

    // Selected always on top, then the rest
    const combined = [...selectedFromAll, ...selectedInFiltered, ...unselectedInFiltered];
    const totalSelected = selectedFromAll.length + selectedInFiltered.length;

    this.totalDevices = combined.length;

    // Paginate
    const start = this.currentPage * this.pageSize;
    this.devices = combined.slice(start, start + this.pageSize);
    this.selectAll = this.devices.length > 0 && this.devices.every((d) => d.selected);
  }

  // --- Filter Handlers ---

  onCompanySearch(text: string): void {
    const search = (text || '').toLowerCase();
    this.filteredCompanies = this.companies.filter((c) =>
      c.name.toLowerCase().includes(search)
    );
  }

  onCompanySelected(event: any): void {
    this.selectedCompany = event.option.value;
    this.onCompanyChange();
  }

  onCompanyBlur(): void {
    // If the text doesn't match any company, reset
    setTimeout(() => {
      if (this.selectedCompany) {
        const match = this.companies.find((c) => c._id === this.selectedCompany);
        if (match) {
          this.companySearchText = match.name;
        }
      } else {
        const textMatch = this.companies.find(
          (c) => c.name.toLowerCase() === (this.companySearchText || '').toLowerCase()
        );
        if (!textMatch) {
          this.companySearchText = '';
        }
      }
    }, 200);
  }

  clearCompany(): void {
    this.companySearchText = '';
    this.selectedCompany = '';
    this.filteredCompanies = [...this.companies];
    this.onCompanyChange();
  }

  displayCompanyName(companyId: string): string {
    if (!companyId) return '';
    const match = this.companies.find((c) => c._id === companyId);
    return match ? match.name : companyId;
  }

  onCompanyChange(): void {
    this.selectedApiKey = '';
    this.selectedFirmwareType = '';
    this.currentPage = 0;
    this.selectedMacs.clear();
    this.loadApiKeys();
    this.loadDevices();
  }

  loadApiKeys(): void {
    if (!this.selectedCompany) {
      this.apiKeys = [];
      this.receiverOptions = [];
      return;
    }
    // Use receivers already loaded in ReceiversService, filtered by company
    const receivers = this.receiversService.receivers || [];
    const companyReceivers = receivers.filter((r) => r.company === this.selectedCompany);

    // Build unique receiver options with name
    const seen = new Set<string>();
    this.receiverOptions = [];
    for (const r of companyReceivers) {
      if (r.apiKey && !seen.has(r.apiKey)) {
        seen.add(r.apiKey);
        this.receiverOptions.push({ apiKey: r.apiKey, name: r.name || r.apiKey });
      }
    }
    this.receiverOptions.sort((a, b) => a.name.localeCompare(b.name));
    this.apiKeys = this.receiverOptions.map((r) => r.apiKey);
  }

  onApiKeyChange(): void {
    this.currentPage = 0;
    this.loadDevices();
  }

  onMacSearch(): void {
    this.macSearchSubject.next(this.macSearch);
  }

  onStatusFilterChange(): void {
    this.currentPage = 0;
    this.selectAll = false;
    this.applyClientFilters();
  }

  get filteredDevices(): PanelDevice[] {
    return this.devices;
  }

  get firmwareTypes(): string[] {
    const types = new Set(this.allDevices.map((d) => d.firmwareType).filter(Boolean));
    return [...types].sort();
  }

  get uniqueFirmwareVersions(): { firmwareType: string; version: string }[] {
    const seen = new Set<string>();
    const result: { firmwareType: string; version: string }[] = [];
    for (const fw of this.availableFirmware || []) {
      const key = `${fw.firmwareType}|${fw.version}`;
      if (!seen.has(key)) {
        seen.add(key);
        result.push({ firmwareType: fw.firmwareType, version: fw.version });
      }
    }
    return result;
  }

  getReceiverName(apiKey: string): string {
    const match = this.receiverOptions.find((r) => r.apiKey === apiKey);
    if (match) return match.name;
    // Fallback to receiversService
    const receiver = (this.receiversService.receivers || []).find((r) => r.apiKey === apiKey);
    return receiver?.name || apiKey;
  }

  onPageChange(event: any): void {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    this.applyClientFilters();
  }

  // --- Selection ---

  toggleSelectAll(): void {
    this.filteredDevices.forEach((d) => {
      d.selected = this.selectAll;
      if (this.selectAll) {
        this.selectedMacs.add(d.mac);
      } else {
        this.selectedMacs.delete(d.mac);
      }
    });
  }

  toggleDevice(device: PanelDevice): void {
    if (device.selected) {
      this.selectedMacs.add(device.mac);
    } else {
      this.selectedMacs.delete(device.mac);
    }
    this.selectAll = this.filteredDevices.length > 0 && this.filteredDevices.every((d) => d.selected);
  }

  getSelectedDevices(): PanelDevice[] {
    return this.allDevices.filter((d) => d.selected);
  }

  getSelectedCount(): number {
    return this.selectedMacs.size;
  }

  // --- Firmware Version Comparison ---

  getUpdateStatus(device: PanelDevice): 'up_to_date' | 'outdated' | 'unknown' {
    if (!device.firmVersion || !this.availableFirmware || this.availableFirmware.length === 0) {
      return 'unknown';
    }
    const matchingFirmware = this.availableFirmware.find(
      (f) => f.firmwareType === device.firmwareType
    );
    if (!matchingFirmware) return 'unknown';

    const deviceVersion = this.normalizeVersion(device.firmVersion);
    const availableVersion = this.normalizeVersion(matchingFirmware.version);

    if (deviceVersion === availableVersion) return 'up_to_date';
    if (deviceVersion < availableVersion) return 'outdated';
    return 'up_to_date'; // newer is also considered up_to_date
  }

  private normalizeVersion(version: string): string {
    return version.replace(/^v/i, '').trim();
  }

  getTargetVersion(device: PanelDevice): string {
    const matchingFirmware = (this.availableFirmware || []).find(
      (f) => f.firmwareType === device.firmwareType
    );
    return matchingFirmware ? matchingFirmware.version : '';
  }

  // --- Update Logic ---

  startUpdate(): void {
    const selected = this.getSelectedDevices();
    if (selected?.length === 0) {
      this.snackBar.open(
        this.translate.instant('FirmwareUpdate.NoMacsProvided'),
        this.translate.instant('Shared.Accept'),
        { duration: 3000 }
      );
      return;
    }

    // Check for reinstall/downgrade scenarios
    const reinstallDevices: { mac: string; currentVersion: string; targetVersion: string }[] = [];
    const downgradeDevices: { mac: string; currentVersion: string; targetVersion: string }[] = [];

    selected.forEach((device) => {
      const targetVersion = this.getTargetVersion(device);
      if (!targetVersion || !device.firmVersion) return;

      const current = this.normalizeVersion(device.firmVersion);
      const target = this.normalizeVersion(targetVersion);

      if (current === target) {
        reinstallDevices.push({
          mac: device.mac,
          currentVersion: current,
          targetVersion: target,
        });
      } else if (current > target) {
        downgradeDevices.push({
          mac: device.mac,
          currentVersion: current,
          targetVersion: target,
        });
      }
    });

    // Show downgrade warning first (higher priority)
    if (downgradeDevices?.length > 0) {
      this.showConfirmDialog('downgrade', downgradeDevices, selected);
      return;
    }

    // Show reinstall warning
    if (reinstallDevices?.length > 0 && reinstallDevices?.length === selected?.length) {
      this.showConfirmDialog('reinstall', reinstallDevices, selected);
      return;
    }

    this.executeUpdate(selected);
  }

  private showConfirmDialog(
    type: 'reinstall' | 'downgrade',
    devices: { mac: string; currentVersion: string; targetVersion: string }[],
    allSelected: PanelDevice[]
  ): void {
    const dialogRef = this.dialog.open(FirmwareConfirmDialogComponent, {
      width: '500px',
      data: { type, devices } as ConfirmDialogData,
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.executeUpdate(allSelected, true);
      }
    });
  }

  private executeUpdate(selected: PanelDevice[], force: boolean = false): void {
    this.isLoading = true;
    const macs = selected.map((d) => d.mac);

    this.firmwareService.startUpdate(macs, force).subscribe({
      next: (res) => {
        this.isLoading = false;
        if (res.success && res.batchId) {
          this.isUpdating = true;
          this.currentView = 'progress';
          // Immediately load the queue to see the new devices
          this.loadGlobalQueue();
          this.startPolling();
          this.snackBar.open(
            this.translate.instant('FirmwareUpdate.UpdateStarted'),
            this.translate.instant('Shared.Accept'),
            { duration: 3000 }
          );
        } else {
          this.snackBar.open(
            res.reason || this.translate.instant('FirmwareUpdate.UpdateFailed'),
            this.translate.instant('Shared.Accept'),
            { duration: 5000 }
          );
        }
      },
      error: () => {
        this.isLoading = false;
        this.snackBar.open(
          this.translate.instant('FirmwareUpdate.UpdateFailed'),
          this.translate.instant('Shared.Accept'),
          { duration: 5000 }
        );
      },
    });
  }

  toggleView(): void {
    this.currentView = this.currentView === 'devices' ? 'progress' : 'devices';
  }

  // --- Queue helpers ---

  /** True if any device in the queue is not in a terminal state */
  get isQueueRunning(): boolean {
    return this.queueDevices.some((d) =>
      !['completed', 'failed', 'rejected'].includes(d.status)
    );
  }

  /** Overall queue status for display */
  get queueStatus(): string {
    if (this.queueDevices.length === 0) return 'empty';
    if (this.isQueueRunning) return 'running';
    const allSuccess = this.queueDevices.every((d) => d.status === 'completed');
    return allSuccess ? 'completed' : 'completed_with_errors';
  }

  // --- Polling ---

  private startPolling(): void {
    if (this.pollingInterval) return;
    this.pollingInterval = setInterval(() => {
      this.firmwareService.getGlobalQueue().subscribe({
        next: (res) => {
          if (res.success && res.data) {
            this.queueDevices = res.data;
            if (this.queueDevices.length === 0 || !this.isQueueRunning) {
              this.stopPolling();
            }
          }
        },
        error: () => { },
      });
    }, 3000);
  }

  private stopPolling(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  // --- Job Helpers ---

  cancelUpdate(): void {
    this.firmwareService.cancelAll().subscribe({
      next: () => {
        this.snackBar.open(
          this.translate.instant('FirmwareUpdate.UpdateCancelled'),
          this.translate.instant('Shared.Accept'),
          { duration: 3000 }
        );
      },
    });
  }

  retryDevice(device: any): void {
    this.firmwareService.retryDevice(device.mac).subscribe({
      next: (res) => {
        if (res.success) {
          device.status = 'waiting';
          device.progress = 0;
          device.error = undefined;
          device.rejectionReason = undefined;

          // Refresh the queue
          this.loadGlobalQueue();
          this.isUpdating = true;
          this.currentView = 'progress';
          this.startPolling();

          this.snackBar.open(
            this.translate.instant('FirmwareUpdate.DeviceRequeued', { mac: device.mac }),
            this.translate.instant('Shared.Accept'),
            { duration: 3000 }
          );
        } else {
          this.snackBar.open(
            res.reason || 'Error al reintentar',
            this.translate.instant('Shared.Accept'),
            { duration: 5000 }
          );
        }
      },
      error: () => {
        this.snackBar.open(
          'Error al reintentar la actualización',
          this.translate.instant('Shared.Accept'),
          { duration: 5000 }
        );
      },
    });
  }

  isRetriable(device: any): boolean {
    return device.status === 'failed' || device.status === 'rejected';
  }

  getStatusLabel(device: any): string {
    const key = `FirmwareUpdate.Status.${device.status}`;
    return this.translate.instant(key);
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'completed':
        return 'status-completed';
      case 'failed':
        return 'status-failed';
      case 'rejected':
        return 'status-rejected';
      case 'queued':
      case 'waiting':
        return 'status-queued';
      default:
        return 'status-active';
    }
  }

  isJobFinished(): boolean {
    return this.queueDevices.length > 0 && !this.isQueueRunning;
  }

  // --- Counter Methods (using queueDevices) ---

  getActiveCount(): number {
    return this.queueDevices.filter((d) =>
      !['waiting', 'completed', 'failed', 'rejected'].includes(d.status)
    ).length;
  }

  getWaitingCount(): number {
    return this.queueDevices.filter((d) => d.status === 'waiting').length;
  }

  getCompletedCount(): number {
    return this.queueDevices.filter((d) => d.status === 'completed').length;
  }

  getFailedCount(): number {
    return this.queueDevices.filter((d) => d.status === 'failed').length;
  }

  getRejectedCount(): number {
    return this.queueDevices.filter((d) => d.status === 'rejected').length;
  }

  getTotalCount(): number {
    return this.queueDevices.length;
  }

  getHistoryCountByStatus(status: string): number {
    return this.historyDevices.filter((d) => d.status === status).length;
  }

  hasFailedDevices(): boolean {
    return this.queueDevices.some((d) => d.status === 'failed');
  }

  retryFailedDevices(): void {
    const failedMacs = this.queueDevices
      .filter((d) => d.status === 'failed')
      .map((d) => d.mac);

    if (failedMacs.length === 0) return;

    this.isLoading = true;
    this.firmwareService.startUpdate(failedMacs, true).subscribe({
      next: (res) => {
        this.isLoading = false;
        if (res.success && res.batchId) {
          this.isUpdating = true;
          this.currentView = 'progress';
          this.loadGlobalQueue();
          this.startPolling();
          this.snackBar.open(
            this.translate.instant('FirmwareUpdate.RetryStarted', { count: failedMacs.length }),
            this.translate.instant('Shared.Accept'),
            { duration: 3000 }
          );
        } else {
          this.snackBar.open(
            res.reason || this.translate.instant('FirmwareUpdate.UpdateFailed'),
            this.translate.instant('Shared.Accept'),
            { duration: 5000 }
          );
        }
      },
      error: () => {
        this.isLoading = false;
        this.snackBar.open(
          this.translate.instant('FirmwareUpdate.UpdateFailed'),
          this.translate.instant('Shared.Accept'),
          { duration: 5000 }
        );
      },
    });
  }

  resetView(): void {
    this.queueDevices = [];
    this.isUpdating = false;
    this.currentView = 'devices';
    this.selectedMacs.clear();
    this.stopPolling();
    this.loadDevices();
  }

  formatMac(mac: string): string {
    return mac ? mac.toUpperCase() : '';
  }
}
